/**
 * Given the current screen and form data, returns the next screen for the warm route
 * @param currentScreen	- The current screen
 * @param formData	- The form data
 * @param _questionSchema - The question schema
 * @returns The next screen
 */
export const getNextScreenWarmRoute = (
	surveyState: NDS.SurveyState,
	formData: NDS.FormData,
	questionSchema: NDS.QuestionSchema,
): NDS.RouteChange => {
	const screenState = surveyState.routeStack[surveyState.routeStack.length - 1];

	const warmDogName = surveyState.warmData?.dog.name as string;
	let dogsState = { ...screenState.dogsState };

	const w2_Options = questionSchema.WARM.W2.fields[0].options;
	const w2_positiveAnswer = w2_Options[0].value;
	const w2_negativeAnswer = w2_Options[1].value;
	const userAnswer = formData.nds_q_w2_still_have_dog;
	const question_w2 =
		userAnswer === w2_positiveAnswer
			? "yes"
			: userAnswer === w2_negativeAnswer
				? "no"
				: "preferNotToSay";

	// Warm Intro
	switch (screenState.screen as WarmScreenNames) {
		case "W0": {
			surveyState.routeStack.push({
				...screenState,
				route: "WARM",
				screen: "W1",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// Previously, you told us about [dogName], is/was this your dog?
		case "W1": {
			const infoCorrectPositiveAnswer =
				questionSchema.WARM.W1.fields[0].options[0].value;
			const warmDogIsTheirs =
				formData.nds_q_w1_info_correct === infoCorrectPositiveAnswer;

			if (warmDogIsTheirs) {
				surveyState.routeStack.push({
					...screenState,
					route: "WARM",
					screen: "W2",
				});
				return {
					surveyState,
					formData,
				};
			}

			surveyState.warmData = undefined;

			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "Q1",
				dogsState: {},
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// Does [dogName] still live with you?
		case "W2": {
			if (question_w2 === "preferNotToSay") {
				surveyState.routeStack.push({
					...screenState,
					route: "ABOUT_YOUR_DOGS",
					screen: "Q1",
				});
				return {
					surveyState,
					formData,
				};
			}
			surveyState.routeStack.push({
				...screenState,
				route: "WARM",
				screen: question_w2 === "yes" ? "W2_2" : "W2_1",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// Why does [dogName] no longer live with you?
		case "W2_1": {
			surveyState.routeStack.push({
				...screenState,
				route: "WARM",
				screen: "W3",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}
		// [dogName] (This is the dogs profile)
		case "W2_2": {
			surveyState.routeStack.push({
				...screenState,
				route: "WARM",
				screen: "W3",
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// Do any other dogs live with you?
		case "W3": {
			const w3_Options = questionSchema.WARM.W3.fields[0].options;
			const w3_PositiveAnswer = w3_Options[0].value;

			const w3_userAnswerHasOtherDogs =
				formData.nds_q_w3_other_dogs === w3_PositiveAnswer;

			// They came from W2_2 so they still have warm dog
			if (question_w2 === "yes") {
				if (w3_userAnswerHasOtherDogs) {
					// They have other dogs so we ask about them
					surveyState.routeStack.push({
						...screenState,
						route: "WARM",
						screen: "W4_1",
					});
					return {
						surveyState,
						formData,
					};
				}

				// Otherwise they go straight to Q6 and we auto populate q1 as "1"

				surveyState.routeStack.push({
					...screenState,
					route: "ABOUT_YOUR_DOGS",
					screen: "Q6",
					dogCount: 1,
				});
				return {
					surveyState,
					formData: { ...formData, nds_q_1_number_of_dogs: "1" },
				};
			}

			// They came from W2_1 so they do not have warm dog
			if (w3_userAnswerHasOtherDogs) {
				// They have answer yes to other dogs so we start them on the cold route
				surveyState.routeStack.push({
					...screenState,
					route: "ABOUT_YOUR_DOGS",
					screen: "Q1",
					dogCount: 0,
				});
				return {
					surveyState,
					formData,
				};
			}

			surveyState.routeStack.push({
				...screenState,
				route: "THOUGHTS_ON_DOGS",
				screen: "E3",
				dogCount: 0,
			});
			return {
				surveyState,
				formData: {
					...formData,
					nds_q_1_number_of_dogs: "0",
					nds_q_e1_ownership_history: "had_dog_as_adult",
					nds_q_e2_how_recent_last_dog: "less_than_12_months",
				},
			};
		}

		// Tell us about the dogs you currently own
		case "W4_1": {
			const numberOfNewDogs = Number.parseInt(
				formData.nds_q_w4_1_amount_new_dogs as string,
			) as 0 | 1 | 2 | 3 | 4 | 5;
			const numberOfDogsTotal = Number.parseInt(
				formData.nds_q_w4_1_amount_total_dogs as string,
			) as 0 | 1 | 2 | 3 | 4 | 5;

			// Clear the data ahead as it may now be wrong
			formData.nds_q_w4_2_new_dog_name_1 = undefined;
			formData.nds_q_w4_2_new_dog_name_2 = undefined;
			formData.nds_q_w4_2_new_dog_name_3 = undefined;
			formData.nds_q_w4_2_new_dog_name_4 = undefined;
			formData.nds_q_w4_2_new_dog_name_1 = undefined;
			formData.nds_q_w4_2_new_dog_name_prefer_not_to_say = undefined;
			formData.nds_q_w4_3_previous_dog_name_2 = undefined;
			formData.nds_q_w4_3_previous_dog_name_3 = undefined;
			formData.nds_q_w4_3_previous_dog_name_4 = undefined;
			formData.nds_q_w4_3_previous_dog_name_5 = undefined;
			formData.nds_q_w4_3_previous_dog_name_prefer_not_to_say = undefined;

			// As we are resetting the form data we need to clear the current dog order to ensure this persists
			dogsState = {
				[`${warmDogName}*!*W`]: {
					index: 1,
					isActive: true, // It remains the active dog while still in the warm route
				},
			};
			// If there are no new dogs, we ask them if they want to tell us about their other dogs
			if (numberOfNewDogs === 0 && numberOfDogsTotal === 1) {
				// Otherwise they go straight to Q6
				surveyState.routeStack.push({
					...screenState,
					route: "ABOUT_YOUR_DOGS",
					screen: "Q6",
					dogCount: numberOfDogsTotal,
					dogsState,
				});
				return {
					surveyState,
					formData,
				};
			}

			// If there are no new dogs, we ask them if they want to tell us about their other dogs
			if (numberOfNewDogs === 0 && numberOfDogsTotal > 1) {
				// Otherwise they go straight to W4_3
				surveyState.routeStack.push({
					...screenState,
					route: "WARM",
					screen: "W4_3",
					dogCount: numberOfDogsTotal,
				});
				return {
					surveyState,
					formData,
				};
			}

			const warmDogIndex = Math.min(numberOfNewDogs + 1, 5) as
				| 1
				| 2
				| 3
				| 4
				| 5;
			dogsState[`${warmDogName}*!*W`] = {
				index: warmDogIndex,
				isActive: true, // It remains the active dog while still in the warm route
				isWarm: true,
			};

			// If they have new dogs we need to get their names
			surveyState.routeStack.push({
				...screenState,
				route: "WARM",
				screen: "W4_2",
				dogCount: numberOfDogsTotal,
				dogsState,
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}

		// What's your new dog's name?
		case "W4_2": {
			// As we are resetting the form data we need to clear the current dog order to ensure this persists

			dogsState = {
				[`${warmDogName}*!*W`]: {
					index: 1,
					isActive: true, // It remains the active dog while still in the warm route
				},
			};
			const numberOfNewDogs = Number.parseInt(
				formData.nds_q_w4_1_amount_new_dogs as string,
			) as 0 | 1 | 2 | 3 | 4;
			const numberOfTotalDogs = Number.parseInt(
				formData.nds_q_w4_1_amount_total_dogs as string,
			);
			const numberOfNewDogsPlusWarm = numberOfNewDogs + 1;

			const warmDogIndex = Math.min(numberOfNewDogsPlusWarm, 5) as
				| 1
				| 2
				| 3
				| 4
				| 5;

			for (let dogIndex = 1; dogIndex < warmDogIndex; dogIndex++) {
				const dogName = formData[
					`nds_q_w4_2_new_dog_name_${dogIndex as 1 | 2 | 3 | 4}`
				] as string;

				const safeDogName =
					!dogName || dogName === "Prefer not to say"
						? {
								1: "your most recent dog",
								2: "your second most recent dog",
								3: "your third most recent dog",
								4: "your fourth most recent dog",
								5: "your fifth most recent dog",
							}[dogIndex]
						: dogName;

				dogsState[`${safeDogName}*!*${dogIndex}`] = {
					index: dogIndex as 1 | 2 | 3 | 4 | 5,
					isActive: false,
				};
			}
			dogsState[`${warmDogName}*!*W`].index = warmDogIndex;
			dogsState[`${warmDogName}*!*W`].isActive = true;
			dogsState[`${warmDogName}*!*W`].isWarm = true;

			if (numberOfNewDogsPlusWarm >= 5) {
				// They can only tell us about 5 dogs so don't offer any more.
				surveyState.routeStack.push({
					...screenState,
					route: "ABOUT_YOUR_DOGS",
					screen: "Q6",
					dogsState,
				});
				return {
					surveyState,
					formData,
				};
			}

			if (numberOfNewDogsPlusWarm === numberOfTotalDogs) {
				// They do not have any old dogs to tell us about

				// Update dogsState so only the lowest dog index is active:
				const updatedDogState = Object.entries(dogsState).reduce(
					(acc, [key, value]) => {
						if (value.index === 1) {
							acc[key] = {
								...value,
								isActive: true,
							};
						} else {
							acc[key] = {
								...value,
								isActive: false,
							};
						}
						return acc;
					},
					{} as NDS.DogState,
				);
				surveyState.routeStack.push({
					...screenState,
					route: "ABOUT_YOUR_DOGS",
					screen: "Q3",
					dogsState: updatedDogState,
				});
				return {
					surveyState,
					formData,
				};
			}

			// Otherwise there is space to ask about more dogs.
			surveyState.routeStack.push({
				...screenState,
				route: "WARM",
				screen: "W4_3",
				dogsState,
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}
		// What's your previously owned dog's name?
		case "W4_3": {
			const numberOfTotalDogs = Number.parseInt(
				formData.nds_q_w4_1_amount_total_dogs as string,
			);

			const numberOfNewDogs = Number.parseInt(
				formData.nds_q_w4_1_amount_new_dogs as string,
			) as 0 | 1 | 2 | 3 | 4;
			const numberOfNewDogsPlusWarm = numberOfNewDogs + 1;

			const warmDogIndex = Math.min(numberOfNewDogsPlusWarm, 5) as
				| 1
				| 2
				| 3
				| 4
				| 5;

			for (
				let dogIndex = warmDogIndex + 1;
				dogIndex <= Math.min(5, numberOfTotalDogs);
				dogIndex++
			) {
				const dogName = formData[
					`nds_q_w4_3_previous_dog_name_${dogIndex as 2 | 3 | 4 | 5}`
				] as string;
				const safeDogName =
					!dogName || dogName === "Prefer not to say"
						? {
								1: "your most recent dog",
								2: "your second most recent dog",
								3: "your third most recent dog",
								4: "your fourth most recent dog",
								5: "your fifth most recent dog",
							}[dogIndex]
						: dogName;

				dogsState[`${safeDogName}*!*${dogIndex}`] = {
					index: dogIndex as 1 | 2 | 3 | 4 | 5,
					isActive: false,
				};
			}

			// If the warm dog is the newest dog we go to Q6
			if (warmDogIndex === 1) {
				surveyState.routeStack.push({
					...screenState,
					route: "ABOUT_YOUR_DOGS",
					screen: "Q6",
					dogsState,
				});
				return {
					surveyState,
					formData,
				};
			}

			// Update dogsState so only the lowest dog index is active:
			const updatedDogState = Object.entries(dogsState).reduce(
				(acc, [key, value]) => {
					if (value.index === 1) {
						acc[key] = {
							...value,
							isActive: true,
						};
					} else {
						acc[key] = {
							...value,
							isActive: false,
						};
					}
					return acc;
				},
				{} as NDS.DogState,
			);
			// Otherwise we go to Q3
			surveyState.routeStack.push({
				...screenState,
				route: "ABOUT_YOUR_DOGS",
				screen: "Q3",
				dogsState: updatedDogState,
			});
			return {
				surveyState: { ...surveyState },
				formData: { ...formData },
			};
		}
	}
};
